import { CLASSIFICATION_TABLE } from '../data/classification';
import { AR_DESCRIPTIONS } from '../data/arDescriptions';
import { loadTarif, lookupTarif, getSubPositions } from './tarifLookup';

function buildStep1Prompt(lang) {
  const L = lang === 'ar' ? 'ARABE' : 'FRANÇAIS';
  return `Tu es un expert en classification tarifaire douanière (Système Harmonisé).
Pour le produit décrit, identifie les 3 POSITIONS tarifaires (4 chiffres) les plus probables.
IMPORTANT: Pense à TOUTES les formes possibles du produit (frais, congelé, transformé, en filets, vivant, etc.)
Par exemple pour "anguilles": 0301 (vivantes), 0302 (fraîches), 0303 (congelées), 0304 (filets), 0305 (séchées/fumées).
Réponds UNIQUEMENT en JSON: {"positions":["XXXX","XXXX","XXXX"],"chapitre":"description en ${L}"}`;
}

function buildStep2Prompt(lang, subPositions) {
  const L = lang === 'ar' ? 'ARABE (العربية)' : 'FRANÇAIS';
  const list = subPositions.map(s => `${s.code} : ${s.description} (DD=${s.dd}%)`).join('\n');
  return `Tu es un expert en classification tarifaire douanière algérienne.
RÈGLE: Tous les textes DOIVENT être en ${L}. JAMAIS en anglais.

Voici les sous-positions EXACTES de la nomenclature algérienne.
CHOISIS le code à 10 chiffres le plus précis ET propose jusqu'à 10 alternatives possibles avec un % de confiance pour chacune.

LISTE DES CODES DISPONIBLES:
${list}

IMPORTANT: Si la description du produit est trop vague pour choisir un seul code, indique-le dans l'explication et propose les codes les plus probables avec leur confiance.

Réponds en JSON:
{
  "code_sh": "XXXX.XX.XX.XX",
  "description": "description enrichie en ${L}",
  "description_ar": "بالعربية",
  "explication": "Explication détaillée en ${L}: pourquoi ce code, dans quelle catégorie/chapitre, et pourquoi pas les autres",
  "explication_ar": "شرح مفصل بالعربية",
  "confiance": 90,
  "note_precision": "${lang === 'ar' ? 'ملاحظة حول دقة الوصف إذا كان غامضاً' : 'Note si la description est trop vague pour un classement précis'}",
  "alternatives": [
    {"code_sh":"XXXX.XX.XX.XX","description":"en ${L}","raison":"en ${L}","confiance":80},
    ...jusqu'à 10 alternatives
  ]
}`;
}

// Strip markdown code fences from LLM response
function cleanJSON(raw) {
  return raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
}

const PROVIDERS = {
  deepseek: {
    call: async (key, sys, prompt) => {
      const resp = await fetch('https://api.deepseek.com/v1/chat/completions', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${key}` },
        body: JSON.stringify({ model: 'deepseek-chat', messages: [{ role: 'system', content: sys }, { role: 'user', content: prompt }], temperature: 0.1 }),
      });
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const d = await resp.json();
      return JSON.parse(cleanJSON(d.choices[0].message.content));
    },
  },
  openai: {
    call: async (key, sys, prompt) => {
      const resp = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${key}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', messages: [{ role: 'system', content: sys }, { role: 'user', content: prompt }], temperature: 0.1, response_format: { type: 'json_object' } }),
      });
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const d = await resp.json();
      return JSON.parse(cleanJSON(d.choices[0].message.content));
    },
  },
};

// Step 1: LLM identifies 4-digit position
// Step 2: Get all 10-digit sub-positions from DGD database
// Step 3: LLM picks the exact code from the real list
async function classifyWithLLM(description, provider, apiKey, lang) {
  const prov = PROVIDERS[provider];
  if (!prov || !apiKey) return null;

  try {
    // STEP 1: Get candidate 4-digit positions (up to 3)
    console.log('[TidjarAI] Step 1: identifying positions for:', description);
    const step1 = await prov.call(apiKey, buildStep1Prompt(lang),
      `Produit: "${description}"`);
    
    // Support both old format {position:"XXXX"} and new {positions:["XXXX","XXXX"]}
    let positions = [];
    if (step1.positions && Array.isArray(step1.positions)) {
      positions = step1.positions.map(p => p.replace(/\./g, '').substring(0, 4)).filter(p => p.length === 4);
    } else if (step1.position) {
      positions = [step1.position.replace(/\./g, '').substring(0, 4)];
    }
    console.log('[TidjarAI] Step 1 result: positions =', positions);
    if (positions.length === 0) return null;

    // STEP 2: Get sub-positions from ALL candidate positions
    let allSubs = [];
    for (const pos of positions) {
      const subs = getSubPositions(pos);
      console.log(`[TidjarAI] Position ${pos}: ${subs.length} sub-positions`);
      allSubs = allSubs.concat(subs);
    }

    if (allSubs.length === 0) {
      // Try 2-digit chapters as fallback
      const chapters = [...new Set(positions.map(p => p.substring(0, 2)))];
      for (const ch of chapters) {
        allSubs = allSubs.concat(getSubPositions(ch));
      }
    }
    if (allSubs.length === 0) return null;

    // Limit to 60 for token efficiency
    const limited = allSubs.slice(0, 60);

    // STEP 3: LLM picks exact code from real DGD list
    console.log('[TidjarAI] Step 3: asking LLM to pick from', limited.length, 'codes');
    const step2 = await prov.call(apiKey, buildStep2Prompt(lang, limited),
      lang === 'ar' ? `المنتج: "${description}"` : `Produit: "${description}"`);
    console.log('[TidjarAI] Step 3 result:', step2.code_sh);

    return normalizeResult(step2, provider);
  } catch (err) {
    console.error('[TidjarAI] LLM error:', err);
    return null;
  }
}

function normalizeResult(r, provider) {
  if (!r.code_sh) return null;
  return {
    code_sh: r.code_sh,
    description: r.description || '', description_ar: r.description_ar || r.description || '',
    explication: r.explication || '', explication_ar: r.explication_ar || r.explication || '',
    confiance: Math.min(100, Math.max(0, r.confiance || 80)),
    note_precision: r.note_precision || '',
    alternatives: Array.isArray(r.alternatives) ? r.alternatives.slice(0, 10) : [],
    source: 'llm', provider,
  };
}

function classifyLocal(description) {
  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const desc = norm(description);
  const raw = description.trim();
  let best = null, bestScore = 0;
  for (const [key, entry] of Object.entries(CLASSIFICATION_TABLE)) {
    let score = 0;
    for (const mot of entry.mots_cles) { if (desc.includes(norm(mot))) score += mot.length; }
    const arDesc = AR_DESCRIPTIONS[key];
    if (arDesc && raw.length > 2) {
      for (const w of arDesc.split(/\s+/).filter(w => w.length > 2)) { if (raw.includes(w)) score += w.length * 2; }
    }
    if (score > bestScore) { bestScore = score; best = { key, ...entry }; }
  }
  if (!best || bestScore < 3) return null;
  return {
    code_sh: best.code_sh_10, categorie: best.key, codes_sh_chapitres: best.codes_sh,
    description: best.description, description_ar: AR_DESCRIPTIONS[best.key] || best.description,
    confiance: Math.min(95, bestScore * 4), source: 'local', alternatives: [],
    explication: `Classification locale (catégorie: ${best.key})`,
    explication_ar: `تصنيف محلي (الفئة: ${best.key})`,
  };
}

function guessCategory(code_sh) {
  const ch = parseInt((code_sh || '').replace(/\./g, '').substring(0, 2));
  const map = {1:'viandes',2:'viandes',3:'poissons',4:'lait_produits_laitiers',5:'viandes',
    6:'fruits_legumes',7:'fruits_legumes',8:'fruits_legumes',9:'cafe_the',10:'ble_cereales',
    11:'ble_cereales',12:'semences',15:'huiles_vegetales',16:'conserves',17:'sucre',
    18:'chocolat_confiserie',19:'pates_alimentaires',20:'conserves',21:'conserves',22:'boissons',
    23:'aliments_animaux',25:'ciment_platre',28:'produits_chimiques_base',29:'produits_chimiques_base',
    30:'produits_pharmaceutiques',31:'engrais',32:'peintures_vernis',33:'cosmetiques',34:'detergents',
    38:'pesticides_phyto',39:'plastiques_granules',40:'caoutchouc_brut',44:'bois_brut',48:'papier_carton',
    52:'textiles_tissus',61:'vetements_confectionnes',62:'vetements_confectionnes',64:'chaussures',
    68:'carrelage_ceramique',69:'carrelage_ceramique',70:'verre_plat',72:'acier_brut',73:'tubes_tuyaux_acier',
    74:'cuivre_brut',76:'aluminium_brut',84:'machines_industrielles',85:'cables_electriques',
    87:'vehicules',90:'equipements_mesure',94:'meubles',95:'jouets'};
  return map[ch] || 'cables_electriques';
}

export async function classifyProduct(description, provider, apiKey, lang = 'fr') {
  await loadTarif();

  let result = null;

  if (provider && apiKey) {
    const llm = await classifyWithLLM(description, provider, apiKey, lang);
    if (llm) {
      const local = classifyLocal(description);
      const cat = local?.categorie || guessCategory(llm.code_sh);
      result = { ...llm, categorie: cat, codes_sh_chapitres: [llm.code_sh.substring(0, 5)], autorisation_prealable: false };
    } else {
      console.log('[TidjarAI] LLM returned null, falling back to local');
    }
  }

  if (!result) {
    result = classifyLocal(description);
    if (!result) console.log('[TidjarAI] Local also returned null for:', description);
  }
  if (!result) return null;

  // Override rates with official tariff DB
  const tarif = lookupTarif(result.code_sh);
  if (tarif) {
    result.taux_DD = tarif.dd;
    result.taux_TVA = tarif.tva;
    result.fap = tarif.fap;
    result.tarif_source = `DGD (${tarif.matched_key})`;
  } else {
    const localEntry = CLASSIFICATION_TABLE[result.categorie];
    result.taux_DD = localEntry?.taux_DD ?? 15;
    result.taux_TVA = localEntry?.taux_TVA ?? 19;
    result.fap = localEntry?.fap ?? [];
    result.tarif_source = 'estimation';
  }

  const localEntry = CLASSIFICATION_TABLE[result.categorie];
  result.daps = localEntry?.daps || false;
  result.daps_taux = localEntry?.daps_taux || 0;

  return result;
}
